// utils/dateUtils.js
import { DateTime } from "luxon";
import Cookies from "universal-cookie";

const cookies = new Cookies();
export const serverZone = cookies.get("serverZone") || "UTC";
export const serverToday = DateTime.now().setZone(serverZone).startOf("day");

// Core Conversions --------------------------------------------------
export const getCurrentServerTime = () => {
  return DateTime.now().setZone(serverZone);
};
export const getCurrentServerTimeISO = () => {
  return DateTime.now().setZone(serverZone).toUTC().toISO();
  // Returns: "2025-08-07T05:07:31.095Z"
};
export const localToServerDate = (localDate) => {
  const dateTime =
    typeof localDate === "string"
      ? DateTime.fromISO(localDate, { zone: "local" })
      : DateTime.fromJSDate(localDate, { zone: "local" });
  return dateTime.setZone(serverZone);
};

// Formatters -------------------------------------------------------
export const formatToDayMonthYear = (date) => {
  return date.toFormat("dd-MM-yyyy");
};

export const formatToMonthYear = (date) => {
  return date.toFormat("MMM yyyy");
};

export const formatDateYear = (timestamp) => {
  return DateTime.fromMillis(timestamp, { zone: serverZone }).toFormat(
    "dd/MM/yyyy"
  );
};

// Date Math --------------------------------------------------------
export const getServerStartOfMonth = () => {
  return getCurrentServerTime().startOf("month");
};

export const getFirstDayOfMonth = (date = getCurrentServerTime()) => {
  return date.startOf("month");
};

// Helpers ----------------------------------------------------------
// Accepts ISO string, epoch millis, epoch seconds, Date, or DateTime, returns DateTime in serverZone
export const toServerDate = (input) => {
  if (!input) {return null;}
  if (input instanceof DateTime) {return input.setZone(serverZone);}
  if (input instanceof Date) {return DateTime.fromJSDate(input).setZone(serverZone);}
  if (typeof input === 'number') {
    // If 13 digits, treat as millis; if 10, treat as seconds (convert to millis)
    if (input > 1e12) {return DateTime.fromMillis(input, { zone: serverZone });}
    if (input > 1e9 && input < 1e12) {return DateTime.fromMillis(input * 1000, { zone: serverZone });}
    // If less than 1e9, treat as seconds (for older epoch values)
    if (input > 0 && input <= 1e9) {return DateTime.fromMillis(input * 1000, { zone: serverZone });}
  }
  if (typeof input === "string") {
    // If string of digits, treat as epoch
    if (/^\d{13}$/.test(input)) {return DateTime.fromMillis(Number(input), { zone: serverZone });}
    if (/^\d{10}$/.test(input)) {return DateTime.fromMillis(Number(input) * 1000, { zone: serverZone });}
    // If less than 10 digits but all digits, treat as seconds
    if (/^\d{1,9}$/.test(input)) {return DateTime.fromMillis(Number(input) * 1000, { zone: serverZone });}
    // Otherwise, try ISO
    const dt = DateTime.fromISO(input, { zone: serverZone });
    if (dt.isValid) {return dt;}
  }
  return null;
};

// Convert server epoch to formatted server time string
export const formatServerTime = (
  dateInput,
  { includeTime = true, format, timezone = serverZone } = {}
) => {
  if (!dateInput) {
    return "N/A";
  }

  try {
    let input = dateInput;
    // If input is a 10-digit epoch (seconds), convert to milliseconds
    if (typeof input === "number" && input > 1e9 && input < 1e12) {
      input = input * 1000;
    }
    if (typeof input === "string" && /^\d{10}$/.test(input)) {
      input = Number(input) * 1000;
    }
    let dt = toServerDate(input);
    if (!dt || !dt.isValid) {throw new Error('Invalid date');}
    // Convert to requested timezone
    dt = timezone === "local" ? dt.toLocal() : dt.setZone(timezone);
    const fmt = format || (includeTime ? "dd/MM/yyyy, HH:mm" : "dd/MM/yyyy");
    return dt.toFormat(fmt);
  } catch (e) {
    console.error("Date formatting error:", {
      input: dateInput,
      error: e.message,
    });
    return "Invalid Date";
  }
};

// For date-only displays (DD/MM/YYYY)
export const formatServerDate = (serverEpoch) => {
  return formatServerTime(serverEpoch, { includeTime: false });
};

export const getServerStartOfDay = () => {
  return DateTime.now().setZone(serverZone).startOf("day");
};

// Accepts any supported date input, returns end of day in serverZone
export const getServerEndOfDay = (dateInput) => {
  const dt = toServerDate(dateInput);
  return dt ? dt.endOf("day") : null;
};

// Alternative version that accepts JS Date
export const convertToServerEndOfDay = (date) => {
  return DateTime.fromJSDate(date, { zone: serverZone }).endOf("day");
};

// initial dates using the serverZone -------------------

const initialStartDate = DateTime.now().setZone(serverZone).startOf("month");
const initialEndDate = DateTime.now().setZone(serverZone);

export const getDefaultSelectedDate = () => ({
  initialStartDate: initialStartDate,
  initialEndDate: initialEndDate,
});

//  Converts ISO date strings into localized timestamps based on server zone -------------------

export const formatDateWithServerZone = (date) => {
  const startDateTime = DateTime.fromISO(date.startDate, {
    zone: "utc",
  }).setZone(serverZone);
  const endDateTime = DateTime.fromISO(date.endDate, { zone: "utc" }).setZone(
    serverZone
  );

  return {
    startDate: startDateTime.toMillis(),
    endDate: endDateTime.toMillis(),
  };
};

// convert epoch to yyyy/mm/dd-------------------------------

export const formatEpochtoYMD = (epoch) => {
  const roundedSeconds = Math.round(epoch / 1000);
  return formatServerTime(roundedSeconds, { format: "yyyy-MM-dd" });
};

// get getepochFromToDate('1 Aug 2025')  => epoch From ToDate -----------------------------------------------

export const getepochFromToDate = (fullDate, { seconds = true } = {}) => {
  const startDateTime = DateTime.fromFormat(
    fullDate, // now "06 Aug 2025"
    "dd MMM yyyy", // expects two-digit day
    { zone: serverZone }
  ).startOf("day");

  const endDateTime = startDateTime.endOf("day");

  const fromDt = seconds
    ? startDateTime.toMillis()
    : Math.round(startDateTime.toMillis() / 1000);
  const toDt = seconds
    ? endDateTime.toMillis()
    : Math.round(endDateTime.toMillis() / 1000);

  return { fromDt, toDt };
};

// get epoch frm to date to(1753977600 1754736007) => month year options [{"value": "Aug 2025","label": "Aug 2025"}] --------------------

export function getEpochtoMonthOptions(fromDt, toDt) {
  const options = [];
  let fromDate = DateTime.fromMillis(fromDt * 1000, {
    zone: serverZone,
  }).startOf("month");
  const toDate = DateTime.fromMillis(toDt * 1000, { zone: serverZone }).startOf(
    "month"
  );

  while (fromDate <= toDate) {
    const monthName = fromDate.toFormat("MMM yyyy");
    options.push({ value: monthName, label: monthName });
    fromDate = fromDate.plus({ months: 1 });
  }
  return options;
}

// Format helper
export const formatMonthYear = (value) => {
  if (!value) {
    return "N/A";
  }

  // Detect type
  if (typeof value === "number") {
    return DateTime.fromSeconds(value, { zone: serverZone }).toFormat(
      "MMM yyyy"
    );
  }
  if (typeof value === "string") {
    return DateTime.fromISO(value, { zone: serverZone }).toFormat("MMM yyyy");
  }
  if (value instanceof Date) {
    return DateTime.fromJSDate(value, { zone: serverZone }).toFormat(
      "MMM yyyy"
    );
  }

  return "Invalid Date";
};

/// 05 Aug 2025 to epoch value converter -------------------

export const convertDateToEpoch = (dateStr) => {
  if (!dateStr) {
    return null;
  }

  const dt = DateTime.fromFormat(dateStr, "dd MMM yyyy", {
    zone: serverZone,
  }).startOf("day");

  return dt.isValid ? dt.toMillis() : null;
};

export function convertEpochTimeToMilliseconds(epochTime) {
  if (epochTime > 1e12) {
    return epochTime;
  } else {
    const epochTimeInMilliseconds = epochTime * 1000;
    return epochTimeInMilliseconds;
  }
}

// Utility to format a date from epoch millis or ISO string to 'YYYY-MM-DD' or readable format
/**
 * Formats a date input (epoch millis/seconds, ISO string, Date, etc) using server timezone.
 * @param {*} dateInput - The date value to format
 * @param {Object} options - Formatting options: { format, includeTime, timezone }
 * @returns {string} Formatted date string or '-'
 */
export function formatDateFromEpochOrISO(
  dateInput,
  { includeTime = true, format, timezone = serverZone } = {}
) {
  // Use formatServerTime for all parsing and formatting logic
  const result = formatServerTime(dateInput, { includeTime, format, timezone });
  return result === "Invalid Date" || result === "N/A" ? "-" : result;
}


export const formatMonth = (str) => {
  return new Date(str).toISOString().slice(0, 7);
}

//converts a Unix epoch timestamp into a readable date format
